import { Skeleton, Space, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useNotification } from "~hooks/useNotification";
import { AddBroadcastButton } from "~pages/Fixtures/BroadcastPage/components/AddBroadcastButton.tsx";
import { useGetBroadcastColumns } from "~pages/Fixtures/BroadcastPage/hooks/useGetBroadcastColumns.tsx";
import { useGetFixtureBroadcast } from "~pages/Fixtures/BroadcastPage/hooks/useGetFixtureBroadcast.ts";
import { useUpsertBroadcast } from "~pages/Fixtures/BroadcastPage/hooks/useUpsertBroadcast.ts";
import type { BroadcastTableRow } from "~pages/Fixtures/BroadcastPage/types/broadcastTableRow.ts";
import type { Broadcast } from "~types/broadcast.ts";
import styles from "./BroadcastPage.module.css";

const BroadcastPage = () => {
  const { mutateAsync: upsertBroadcast, isPending: isUpserting } = useUpsertBroadcast();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  const { data, isLoading: isLoadingFixture, isFetching: isFetchingFixture } = useGetFixtureBroadcast();

  const [broadcasts, setBroadcasts] = useState<Array<BroadcastTableRow>>([]);
  const [searchValue, setSearchValue] = useState("");

  const filteredBroadcasts = broadcasts.filter((b) => b.broadcaster?.toLowerCase().includes(searchValue.toLowerCase()));

  useEffect(() => {
    if (data) {
      setBroadcasts((data?.broadcasts || []).map((item, id) => ({ ...item, id })));
    }
  }, [data]);

  const handleDelete = async (broadcast: BroadcastTableRow) => {
    const filteredBroadcasts = broadcasts.filter((b) => b.id !== broadcast.id);
    await upsertBroadcast(filteredBroadcasts);
    notification.success({
      message: formatMessage({ id: "broadcast.delete.success" }),
      placement: "bottomRight",
    });
  };

  const handleSave = async () => {
    const updated = broadcasts.map((item) => {
      const updatedItem = filteredBroadcasts.find((b) => b.id === item.id);
      return updatedItem ? updatedItem : item;
    });
    await upsertBroadcast(updated);
  };

  const handleDiscard = () => {
    setBroadcasts((data?.broadcasts || []).map((item, id) => ({ ...item, id })));
  };

  const handleAdd = async (data: Array<Broadcast>) => {
    await upsertBroadcast(data);
    notification.success({
      message: formatMessage({ id: "broadcast.add.success" }),
      placement: "bottomRight",
    });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const columns = useGetBroadcastColumns(handleDelete, handleSave, handleDiscard, isUpserting);

  return (
    <Page backButtonTextKey="broadcast.back">
      <Skeleton loading={isLoadingFixture}>
        <div
          id="broadcast-page"
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Typography.Title level={2}>
            {" "}
            {formatMessage(
              { id: "broadcast.listing" },
              {
                fixtureName: data?.fixtureName,
                startDate: dayjs(data?.startTimeLocal).format("DD/MM/YYYY"),
              },
            )}
          </Typography.Title>
          <Space>
            <SearchFilter
              initial={searchValue}
              searchValue={searchValue}
              doSearch={(value) => handleSearch(value)}
              placeholderIntlKey="search.two.letters"
            />
            <PermissionCheck permissionKeys={[PermissionNames.FIXTURE]} permissionOptions={[PermissionOperations.UPDATE]}>
              <AddBroadcastButton broadcasts={broadcasts} onAdd={handleAdd} loading={isUpserting} />
            </PermissionCheck>
          </Space>
        </div>
        <Table
          size="small"
          dataSource={filteredBroadcasts}
          columns={columns}
          loading={isFetchingFixture || isUpserting}
          rowKey="id"
          rootClassName={styles.tableContainer}
          scroll={{ x: "max-content" }}
          pagination={false}
        />
      </Skeleton>
    </Page>
  );
};

export default BroadcastPage;
